import {
  CloseOutlined,
  MoreOutlined,
  SyncOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Button, ButtonProps } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { GoHighLevelIcon } from '@mm/icons';
import { useGoHighLevelCRUDActions } from '@mm/state/actions/GoHighLevelCRUDActions';
import { useSuppressionListsCRUDActions } from '@mm/state/actions/SuppressionListsCRUDActions';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { selectGoHighLevelSuppressionList } from '@mm/state/modules/suppressionListsTableState/selectors';
import { selectCrmsLeadsSent } from '@mm/state/selectors/connectionsSelectors';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Dropdown } from 'antd';
import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { Help } from '../KlaviyoIntegrations/KlaviyoHelpLink';
import { OauthTokenStatus } from '../OauthTokenStatus/OauthTokenStatus';
import styles from './GoHighLevelIntegrations.module.css';
import { GoHighLevelSettings } from './GoHighLevelSettings';
import { GoHighLevelSuppressionSetup } from './GoHighLevelSuppressionSetup';
import { useConnectGoHighLevel } from './use-connect-gohighlevel';

export const tokenStatus = (
  token: ConnectionModel['gohighlevelOauthToken'],
) => <OauthTokenStatus token={token} styles={styles} />;

export const GoHighLevelIntegration = ({
  version,
}: {
  version: 'connections' | 'suppression';
}) => {
  const accounts = useSelector(
    (state: StoreState) => state.goHighLevel.accounts,
  );
  const suppressions = useSelector(selectGoHighLevelSuppressionList);

  const suppression = suppressions[0];

  const [accountsFetched, setAccountsFetched] = useState(false);
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const oAuthButtonName = 'Add a GoHighLevel Account';
  const otherProps = {
    landmineName: landmineNames.GOHIGHLEVEL_UPGRADE,
    featureFlag: 'nativeIntegrations',
  };
  const { invalidateStore } = useGoHighLevelCRUDActions();
  const {
    getGoHighLevelAccounts,
    goHighLevelConnections,
    getGoHighLevelConnections,
    connectGoHighLevelToken,
    revoke,
  } = useConnectGoHighLevel();
  const usedToken = goHighLevelConnections?.[0]?.gohighlevelOauthToken;
  const usedTokenId = usedToken?.id;
  const connectDisabled = (accounts || []).find(
    acc => acc.id === usedTokenId,
  )?.readOnly;

  if (usedToken) {
    usedToken.readOnly = !!connectDisabled;
  }

  const connectToken = async () => {
    await connectGoHighLevelToken(usedTokenId);
  };

  useEffect(() => {
    if (accountsFetched) {
      getGoHighLevelConnections();
    }
  }, [accountsFetched]);

  useEffect(() => {
    (async () => {
      await getGoHighLevelAccounts();
      setAccountsFetched(true);
    })();
    return () => {
      invalidateStore();
    };
  }, []);

  const oAuthButton = (
    buttonName?: string | ReactNode,
    buttonProps?: Pick<ButtonProps, 'size' | 'color' | 'css'>,
  ) => {
    const defaultButtonProps = {
      css: {
        container: 'integrations__header-add-button-secondary',
      },
    };

    return (
      <Button
        {...(buttonProps || defaultButtonProps)}
        onClick={e => {
          e?.stopPropagation();
          connectToken();
        }}
      >
        {buttonName || 'Set Up Connection'}
      </Button>
    );
  };

  const { invalidateStore: resetSuppressionsStore } =
    useSuppressionListsCRUDActions();

  const stopPropagation = (e: React.SyntheticEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const menuItems = [
    {
      key: '1',
      label: (
        <div
          className={styles.menuItem}
          onClick={e => {
            stopPropagation(e);
            connectToken();
            resetSuppressionsStore();
          }}
        >
          <SyncOutlined />
          Reconnect
        </div>
      ),
    },
    {
      key: '2',
      label: (
        <div
          className={styles.menuItem}
          onClick={e => {
            // stopPropagation(e);
            revoke(usedTokenId as number);
            resetSuppressionsStore();
          }}
        >
          <CloseOutlined />
          Disconnect
        </div>
      ),
    },
  ];

  const crmsLeadsSent = useSelector(selectCrmsLeadsSent);
  const leadsSent = crmsLeadsSent?.gohighlevelContactsSent;

  const header = (
    <div className={styles.integrationHeading}>
      <Help
        link="https://customers.ai/help/l/en/article/a0ro35f54w-highlevel-integration"
        text="Help Center"
      />
      {connectDisabled ? (
        tokenStatus(usedToken)
      ) : usedToken ? (
        <div className={styles.integrationHeading}>
          {version === 'suppression' ? (
            <>
              <span>Profiles</span>
              <span className={styles.suppressionsCount}>
                {suppression?.profilesCount || 0}
              </span>
            </>
          ) : leadsSent ? (
            <div className="integrations__header-status">
              <TeamOutlined className="integrations__header-status-icon" />
              <div className="integrations__header-status-count">
                {leadsSent}
              </div>
              Leads sent
            </div>
          ) : null}

          <div className={styles.tokenStatus}>
            {tokenStatus(usedToken)}
            <div onClick={stopPropagation}>
              <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                <MoreOutlined alt="menu ellipsis" />
              </Dropdown>
            </div>
          </div>
        </div>
      ) : (
        oAuthButton()
      )}
    </div>
  );

  return (
    <BaseIntegration
      titleComponent={
        <BaseIntegrationHeader
          title="HighLevel"
          upgradeButtonName={
            !connectDisabled ? 'Add an Account' : 'Add a Connection'
          }
          icon={
            <GoHighLevelIcon className="integrations__gohighlevel-header-icon" />
          }
          headerRightComponent={header}
          {...otherProps}
        />
      }
      integrationType="gohighlevel"
      emptyStateConfig={{
        button: hasNativeIntegrationsFeature
          ? oAuthButton(oAuthButtonName, {
              css: { container: 'integrations__empty-state-button' },
            })
          : oAuthButtonName,
        icon: (
          <GoHighLevelIcon className="integrations__gohighlevel-icon-big" />
        ),
        ...otherProps,
      }}
      showEmptyState={!usedToken}
    >
      {usedToken &&
        (version === 'suppression' ? (
          <GoHighLevelSuppressionSetup
            connectDisabled={!!connectDisabled}
            tokenId={usedToken.id}
          />
        ) : (
          <GoHighLevelSettings
            connections={goHighLevelConnections}
            account={usedToken}
          />
        ))}
    </BaseIntegration>
  );
};
